import React, { Fragment, useEffect, useState } from 'react';
import { Row, Col, Button, Input, Pagination, Popconfirm, message, Checkbox, Select } from 'antd';
import { QuestionCircleOutlined, SortDescendingOutlined, SortAscendingOutlined } from '@ant-design/icons';
import { statusColor, PAGE_SIZE_OPTIONS } from "helpers/commonFunctions";
import { Link } from 'react-router-dom';
import Image from 'helpers/Image';
import BulkUploadModal from "./bulkUpload";
import "../style.css";
import { useEmployeeListing, useDeleteEmployee, useActiveOrInactive } from 'hooks/configuration/employee/employee';
import TableStateRow from "components/tableState";
import StatusBadge from "components/statusBadge";
import { PAGES, ACTIONS, usePermissions } from "hooks/permissions/permissions";

const Employee = () => {

    const { can } = usePermissions();
    const canAdd = can(PAGES.EMPLOYEE, ACTIONS.ADD);
    const canEdit = can(PAGES.EMPLOYEE, ACTIONS.EDIT);
    const canDelete = can(PAGES.EMPLOYEE, ACTIONS.DELETE);
    const showActions = canEdit || canDelete;
    // Row selection exists only for the bulk Active/Inactive buttons, so it goes with EDIT.
    const columnCount = 6 + (canEdit ? 1 : 0) + (showActions ? 1 : 0);

    const [page, setPage] = useState(50);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { mutate: getEmployee, data: EmployeeData, isLoading: Loading } = useEmployeeListing();
    const { mutate: makeActiveOrInactiveEmployee } = useActiveOrInactive();
    const [listData, setListData] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [searchKey, setSearchKey] = useState("");
    const [selectedEmployee, setSelectedEmployee] = useState([]);
    const [sortField, setSortField] = useState("");
    const [defaultCall, setDefaultCall] = useState(false);
    const [sortMethod, setSortMethod] = useState("desc");
    // ALL keeps the full register; ACTIVE matches the dashboard's employee count.
    const [status, setStatus] = useState("ALL");
    const [initValues, setInitValues] = useState({
        "pageNumber": pageNumber,
        "pageSize": page,
        "searchKey": searchKey,
        "sortField": sortField,
        "sortMethod": sortMethod,
        "status": status
    })
    const [sorting, setSorting] = useState({
        "ts.card_id": null,
        "e.name": null,
        "e.fin_no": null,
        "e.designation": null,
        "d.department_name": null,
        "e.active": null
    });
    const { mutate: deleteEmployee, isLoading: deleteLoading } = useDeleteEmployee();
    const onSortChange = async (column) => {
        initValues.pageNumber = 0;
        initValues.sortField = column;
        initValues.sortMethod = sorting[column] === 'asc' ? 'desc' : 'asc';
        setPageNumber(0);
        const newSorting = {
            ...sorting,
            [column]: sorting[column] === 'asc' ? 'desc' : 'asc',
        };
        setSorting(newSorting);
        setInitValues(initValues);
        setSortField(column);
        setSortMethod(newSorting[column]);
        setListData([]);
        setSelectedEmployee([]);
    };

    const deleteId = (id) => {
        deleteEmployee(id, {
            onSuccess: (item) => {
                if (item.statusCode === 200) {
                    message.success(item.data);
                    setSelectedEmployee((prev) => prev.filter((employeeId) => employeeId !== id));
                    onFetchListEmployee(initValues);
                }
                else {
                    message.error(item?.data);
                }
            },
        });
    }
    const handleCloseModal = () => {
        setIsModalVisible(false);
    };

    const handleClickOpen = () => {
        setIsModalVisible(true);
    };

    const onFetchListEmployee = (values) => {
        const nformData = JSON.stringify(values);
        getEmployee(nformData, {
            onSuccess: (data) => {
                setListData(data?.data?.content)
            }
        });
    }

    const activeOrInactive = (value, employeeId) => {
        const formData = {
            employeeIds: employeeId ? employeeId : selectedEmployee,
            active: value
        }
        makeActiveOrInactiveEmployee(JSON.stringify(formData), {
            onSuccess: (item) => {
                if (item.statusCode === 200) {
                    message.success(item.data);
                    setSelectedEmployee([]);
                    onFetchListEmployee(initValues);
                }
                else {
                    message.error(item?.data);
                }
            }
        })
    }

    // Selection only covers the rows on screen, so anything that replaces
    // those rows (paging, sorting, searching) has to clear it too -- otherwise
    // the Active/Inactive buttons act on employees the user can no longer see.
    const onChange = (page) => {
        initValues.pageNumber = page - 1;
        setInitValues(initValues);
        setPageNumber(page - 1);
        setSelectedEmployee([]);
    }

    const onShowSizeChange = (current, pageSize) => {
        setPage(initValues.pageSize = pageSize)
        setSelectedEmployee([]);
    }

    const searchHandler = (e) => {
        initValues.searchKey = e.target.value;
        let data = { ...initValues };
        data.searchKey = e.target.value;
        data.pageNumber = e.target.value !== "" ? 0 : initValues.pageNumber;
        setInitValues(data);
        setSearchKey(e.target.value);
        setSelectedEmployee([]);
    }

    const statusHandler = (value) => {
        setInitValues({ ...initValues, status: value, pageNumber: 0 });
        initValues.status = value;
        initValues.pageNumber = 0;
        setPageNumber(0);
        setStatus(value);
        setSelectedEmployee([]);
    }

    const handleSelectEmployee = (e, employeeId) => {
        if (employeeId) {
            if (selectedEmployee?.includes(employeeId)) {
                setSelectedEmployee(selectedEmployee.filter((id) => id !== employeeId));
            } else {
                setSelectedEmployee([...selectedEmployee, employeeId]);
            }
        } else {
            if (e.target.checked === true) {
                const allEmployeeIds = (listData || []).map(item => item.employeeId);
                setSelectedEmployee(allEmployeeIds);
            } else {
                setSelectedEmployee([]);
            }
        }
    }

    const checkAll = () => {
        if (!listData?.length) {
            return false;
        }
        return listData.every(item => selectedEmployee.includes(item?.employeeId));
    }

    const checkItem = (employeeId) => {
        return selectedEmployee.includes(employeeId);
    }

    const checkEmpty = () => {
        return selectedEmployee.length === 0;
    }
    useEffect(() => {
        setDefaultCall(true);
    }, [])

    useEffect(() => {
        if (defaultCall) {
            onFetchListEmployee(initValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultCall, pageNumber, searchKey, page, sortField, sortMethod, status]);


    return (
        <Fragment>
            <div className="common-head employee-config">
                <div className="title">
                    <h1>Employees</h1>
                    <p>The workforce register, with card and shift assignment</p>
                </div>
                <div className="txt-right single-line">
                    <Select value={status} onChange={statusHandler} style={{ minWidth: 130 }}
                        options={[
                            { label: "All employees", value: "ALL" },
                            { label: "Active", value: "ACTIVE" },
                            { label: "Inactive", value: "INACTIVE" },
                        ]} />
                    <Input placeholder='Search' onChange={searchHandler} prefix={<Image className="search-img" name={"search.svg"} />} allowClear />
                    {!canAdd ? null : <Link to="/configuration/employee/create">
                        <Button><Image name="plus.png" /> New Employee</Button>
                    </Link>
                    }
                    {!canAdd ? null : <Button className='bulk-upload-btn' onClick={handleClickOpen}>Bulk Upload</Button>}

                    {canEdit && checkEmpty() === false && <Popconfirm Loading={deleteLoading}
                        title={`Are you sure to active ${selectedEmployee.length === 1 ? "employee" : "employees"}?`}
                        onConfirm={() => activeOrInactive(true)}
                        icon={
                            <QuestionCircleOutlined />
                        }
                        okText="Yes"
                        cancelText="No"><Button className='active-button'> Active </Button></Popconfirm>}
                    {canEdit && checkEmpty() === false && <Popconfirm Loading={deleteLoading}
                        title={`Are you sure to inactive ${selectedEmployee.length === 1 ? "employee" : "employees"}?`}
                        onConfirm={() => activeOrInactive(false)}
                        icon={
                            <QuestionCircleOutlined />
                        }
                        okText="Yes"
                        cancelText="No"><Button className='inactive-button'> Inactive </Button></Popconfirm>}
                </div>
            </div>
            <div className="common-table">
                <table>
                    <thead>
                        <tr>
                            {canEdit ? <th>
                                <Checkbox checked={checkAll()} style={{ paddingLeft: 25 }} onChange={(e) => handleSelectEmployee(e)} />
                            </th> : null}
                            <th>Card ID {sorting["ts.card_id"] === 'asc' ? <SortAscendingOutlined onClick={() => onSortChange("ts.card_id")} /> : <SortDescendingOutlined onClick={() => onSortChange("ts.card_id")} />}</th>
                            {/* <th>Turnstile Employee ID {sorting["Employee-e.employee_id"] === 'asc' ? <SortAscendingOutlined onClick={() => onSortChange("Employee-e.employee_id")} /> : <SortDescendingOutlined onClick={() => onSortChange("Employee-e.employee_id")} />}</th> */}
                            <th>Employee Name {sorting["e.name"] === 'asc' ? <SortAscendingOutlined onClick={() => onSortChange("e.name")} /> : <SortDescendingOutlined onClick={() => onSortChange("e.name")} />}</th>
                            <th>Fin No {sorting["e.fin_no"] === 'asc' ? <SortAscendingOutlined onClick={() => onSortChange("e.fin_no")} /> : <SortDescendingOutlined onClick={() => onSortChange("e.fin_no")} />}</th>
                            <th>Designation {sorting["e.designation"] === 'asc' ? <SortAscendingOutlined onClick={() => onSortChange("e.designation")} /> : <SortDescendingOutlined onClick={() => onSortChange("e.designation")} />}</th>
                            <th>Contractor {sorting["d.department_name"] === 'asc' ? <SortAscendingOutlined onClick={() => onSortChange("d.department_name")} /> : <SortDescendingOutlined onClick={() => onSortChange("d.department_name")} />}</th>

                            {/* <th>Shift time </th> */}
                           
                            <th>Status {sorting["e.active"] === 'asc' ? <SortAscendingOutlined onClick={() => onSortChange("e.active")} /> : <SortDescendingOutlined onClick={() => onSortChange("e.active")} />}</th>
                            {showActions ? <th>Action</th> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {Loading ? (
                            <TableStateRow colSpan={columnCount} loading />
                        ) : listData == null || listData === undefined || listData?.length === 0 ? (
                            <TableStateRow colSpan={columnCount}>No employees found</TableStateRow>
                        ) : listData.map((item, a) => {
                                /* const [startTime, endTime] = item?.shiftTime?.split(' - ');
                                const formattedStartTime = `${startTime} am`;
                                const formattedEndTime = `${endTime} pm`; */
                                return (
                                    <tr key={a}>
                                        {canEdit ? <td>
                                            <Checkbox checked={checkItem(item?.employeeId)} style={{ paddingLeft: 25 }} onChange={(e) => handleSelectEmployee(e, item?.employeeId)} />
                                        </td> : null}
                                        <td>{item?.cardId}</td>
                                        {/* <td>{item?.turnstileEmployeeId}</td> */}
                                        <td>{item?.employeeName}</td>
                                        <td>{item?.finNo}</td>
                                        <td>{item?.designation}</td>
                                        <td>{item?.company}</td>
                                        
                                        {/* <td></td> */}

                                        <td>{canEdit ? <Popconfirm Loading={deleteLoading}
                                            title={`Are you sure to ${item?.active === true ? "inactive" : "active"} ${item?.employeeName}?`}
                                            onConfirm={() => activeOrInactive(!item?.active, [item?.employeeId])}
                                            icon={
                                                <QuestionCircleOutlined />
                                            }
                                            okText="Yes"
                                            cancelText="No"><button className={statusColor(item.active)}>
                                                {item?.active ? "Active" : "InActive"}
                                            </button></Popconfirm> : <StatusBadge active={item?.active} />}</td>
                                        {showActions ? <td>
                                            {!canEdit ? null : <Link to={`/configuration/employee/${item?.employeeId}`} state={item}>
                                                <Image name="edit.png" />
                                            </Link>}
                                            {!canDelete ? null : <Popconfirm
                                                Loading={deleteLoading}
                                                title={`Are you sure to delete ${item?.employeeName}?`}
                                                onConfirm={() => deleteId(item?.employeeId)}
                                                icon={
                                                    <QuestionCircleOutlined />
                                                }
                                                okText="Yes"
                                                cancelText="No"
                                            >
                                                <Image name="delete.png" />
                                            </Popconfirm>}
                                        </td> : null}
                                    </tr>
                                )
                            })}
                    </tbody>
                </table>
            </div>
            <Row className="table-pagination">
                <Col md={12}>
                    <h5>Showing {pageNumber === 0 ? 1 : (EmployeeData?.data?.pageable?.offset + 1 || 0)} to {page === 0 ? (EmployeeData?.data?.pageable?.offset + listData?.length || 0) : (EmployeeData?.data?.pageable?.offset + EmployeeData?.data?.numberOfElements || 0)} of {EmployeeData?.data?.totalElements} entries</h5>
                </Col>
                <Col md={12} className="txt-right">
                    {EmployeeData?.data?.content?.length === 0 ? null : <Pagination pageSize={page} showSizeChanger pageSizeOptions={PAGE_SIZE_OPTIONS} onChange={onChange} current={pageNumber + 1} onShowSizeChange={onShowSizeChange} total={EmployeeData?.data?.totalElements} />}
                </Col>
            </Row>
            {!canAdd ? null : <BulkUploadModal open={isModalVisible} onClose={handleCloseModal} />}
        </Fragment >
    )
}

export default Employee;
